import { TextField, FormControlLabel, Checkbox, InputAdornment, FormControl } from "@mui/material";
import "../styles/registration.scss";
import { PasswordInput } from "./PasswordInput";
import React, { useState, useEffect } from "react";
import { IMaskInput } from 'react-imask';

const PhoneMask = React.forwardRef(function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <IMaskInput
            {...other}
            mask="(000) 000-00-00"
            definitions={{
                '#': /[1-9]/,
            }}
            inputRef={ref}
            onAccept={(value) => onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

export const RegistrationStep1 = React.forwardRef((props, ref) => {
    const { setIsFormValid } = props;
    const [fullName, setFullName] = useState(null);
    const [phone, setPhone] = useState(null);
    const [email, setEmail] = useState(null);
    const [passportSeries, setPassportSeries] = useState(null);
    const [passportNumber, setPassportNumber] = useState(null);
    const [password, setPassword] = useState(null);
    const [confirmPassword, setConfirmPassword] = useState(null);
    const [userAgreement, setUserAgreement] = useState(false);
    const [personalData, setPersonalData] = useState(false);
    const [auctionRules, setAuctionRules] = useState(false);

    const [errors, setErrors] = useState({
        fullName: "",
        phone: "",
        email: "",
        passportNumber: "",
        passportSeries: "",
        password: "",
        confirmPassword: "",
        userAgreement: false,
        personalDataAgreement: false,
        auctionRulesAgreement: false
    });

    const formatPhone = (inputPhone) => {
        return `+7${inputPhone.replace(/[^0-9+]/g, '')}`
    }

    React.useImperativeHandle(ref, () => ({
        getData: () => {
            return {
                fullName: fullName,
                phone: formatPhone(phone),
                email: email,
                passportSeries: passportSeries,
                passportNumber: passportNumber,
                password: password
            }
        }
    }));

    const validateFullName = (name) => {
        if (!name?.trim()) {
            return "ФИО обязательно для заполнения";
        }

        const words = name.trim().split(/\s+/);
        if (words.length < 2) {
            return "Введите фамилию, имя и отчество через пробел";
        }

        const nameRegex = /^[А-Яа-яЁёA-Za-z\s-]+$/;
        if (!nameRegex.test(name)) {
            return "ФИО может содержать только буквы, пробелы и дефисы";
        }

        return "";
    };

    const validateEmail = (mail) => {
        if (!mail?.trim()) {
            return "Email обязателен для заполнения";
        }
        const mailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        if (!mailRegex.test(mail)) {
            return "Введите корректный адрес электронной почты";
        }

        return "";
    };

    const validatePassportSeries = (series) => {
        if (!series?.trim()) {
            return "Серия паспорта обязательна для заполнения";
        }
        const cleanSeries = series.replace(/\s+/g, '');

        const seriesRegex = /^\d{4}$/;
        if (!seriesRegex.test(cleanSeries)) {
            return "Серия паспорта должна состоять из 4 цифр";
        }

        return "";
    };

    const validatePassportNumber = (number) => {
        if (!number?.trim()) {
            return "Номер паспорта обязателен для заполнения";
        }
        const cleanNumber = number.replace(/\s+/g, '');

        const numberRegex = /^\d{6}$/;
        if (!numberRegex.test(cleanNumber)) {
            return "Номер паспорта должен состоять из 6 цифр";
        }

        return "";
    };

    const validatePhone = (phoneNumber) => {
        if (!phoneNumber?.trim()) {
            return "Номер телефона обязателен для заполнения";
        }

        const cleanPhone = phoneNumber.replace(/[()\s-]/g, '');

        if (cleanPhone.length !== 10) {
            return "Номер телефона должен содержать 10 цифр";
        }

        const phoneRegex = /^\d+$/;
        if (!phoneRegex.test(cleanPhone)) {
            return "Номер телефона должен содержать только цифры";
        }

        return "";
    };

    const validatePassword = (pass) => {
        if (!pass) {
            return "Пароль обязателен для заполнения";
        }

        if (pass.length < 8) {
            return "Пароль должен содержать минимум 8 символов";
        }

        return "";
    };

    const validateConfirmPassword = (confirmPass) => {
        if (password !== confirmPass) {
            return "Пароли не совпадают";
        }
        return "";
    };

    const validateCheckbox = (isChecked, fieldName) => {
        if (!isChecked) {
            switch (fieldName) {
                case 'userAgreement':
                    return "Необходимо принять пользовательское соглашение";
                case 'personalData':
                    return "Необходимо согласиться на обработку персональных данных";
                case 'auctionRules':
                    return "Необходимо принять правила аукциона";
                default:
                    return "";
            }
        }
        return "";
    };

    const validateField = (fieldName, value) => {
        let error = "";

        switch (fieldName) {
            case 'fullName':
                error = validateFullName(value);
                break;
            case 'phone':
                error = validatePhone(value);
                break;
            case 'email':
                error = validateEmail(value);
                break;
            case 'passportSeries':
                error = validatePassportSeries(value);
                break;
            case 'passportNumber':
                error = validatePassportNumber(value);
                break;
            case 'password':
                error = validatePassword(value);
                if (confirmPassword && value !== confirmPassword) {
                    setErrors(prev => ({
                        ...prev,
                        confirmPassword: "Пароли не совпадают"
                    }));
                }
                break;
            case 'confirmPassword':
                error = validateConfirmPassword(value);
                break;
            case 'userAgreement':
                error = validateCheckbox(value, 'userAgreement');
                break;
            case 'personalData':
                error = validateCheckbox(value, 'personalData');
                break;
            case 'auctionRules':
                error = validateCheckbox(value, 'auctionRules');
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
    };

    const validateForm = () => {
        const newErrors = {
            fullName: validateFullName(fullName),
            phone: validatePhone(phone),
            email: validateEmail(email),
            passportSeries: validatePassportSeries(passportSeries),
            passportNumber: validatePassportNumber(passportNumber),
            password: validatePassword(password),
            confirmPassword: validateConfirmPassword(confirmPassword),
            userAgreement: validateCheckbox(userAgreement, 'userAgreement'),
            personalData: validateCheckbox(personalData, 'personalData'),
            auctionRules: validateCheckbox(auctionRules, 'auctionRules')
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(error => error !== "");
        setIsFormValid(!hasErrors);

        return !hasErrors;
    };

    const handleFullNameChange = (event) => {
        const value = event.target.value;
        setFullName(value);
        validateField('fullName', value);
    };

    const handlePhoneChange = (event) => {
        const value = event.target.value;
        setPhone(value);
        validateField('phone', value);
    };

    const handleEmailChange = (event) => {
        const value = event.target.value;
        setEmail(value);
        validateField('email', value);
    };
        
    const handlePassportSeriesChange = (event) => {
        const value = event.target.value;
        setPassportSeries(value);
        validateField('passportSeries', value);
    };
        
    const handlepassportNumberChange = (event) => {
        const value = event.target.value;
        setPassportNumber(value);
        validateField('passportNumber', value);
    };

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        setPassword(value);
        validateField('password', value);
    };

    const handleConfirmPasswordChange = (event) => {
        const value = event.target.value;
        setConfirmPassword(value);
        validateField('confirmPassword', value);
    };

    const handleUserAgreementChange = (event) => {
        const isChecked = event.target.checked;
        setUserAgreement(isChecked);
        validateField('userAgreement', isChecked);
    };

    const handlePersonalDataChange = (event) => {
        const isChecked = event.target.checked;
        setPersonalData(isChecked);
        validateField('personalData', isChecked);
    };

    const handleAuctionRulesChange = (event) => {
        const isChecked = event.target.checked;
        setAuctionRules(isChecked);
        validateField('auctionRules', isChecked);
    };

    useEffect(() => {
        validateForm();
    }, [fullName, phone, email, passportSeries, passportNumber, password, confirmPassword, userAgreement, personalData, auctionRules]);


    return (
        <React.Fragment>
            {/* Скрытые поля, для автозаполнения от браузера */}
            <div style={{ opacity: 0, position: 'absolute', height: 0, overflow: 'hidden', zIndex: -1 }}>
                <TextField type="email" name="email" tabIndex="-1" />
                <TextField type="password" name="password" tabIndex="-1" />
            </div>
            <TextField
                label="Фамилия Имя Отчество"
                value={fullName ?? ""}
                onChange={handleFullNameChange}
                className="registration__input-element"
                error={!!errors.fullName && fullName !== null}
                helperText={fullName !== null && errors.fullName}
                required
                fullWidth
                margin="normal"
            />
            <TextField
                value={phone ?? ""}
                onChange={handlePhoneChange}
                label="Номер телефона"
                className="registration__input-element"
                error={!!errors.phone && phone !== null}
                helperText={phone !== null && errors.phone}
                slotProps={{
                    input: {
                        inputComponent: PhoneMask,
                        startAdornment: <InputAdornment position="start">+7</InputAdornment>
                    },
                    htmlInput: {
                        inputMode: 'numeric'
                    }
                }}
                required
                fullWidth
                margin="normal"
            />
            <TextField
                value={email ?? ""}
                onChange={handleEmailChange}
                label="Электронная почта"
                className="registration__input-element"
                type="email"
                name="email"
                error={!!errors.email && email !== null}
                helperText={email !== null && errors.email}
                required
                fullWidth
                margin="normal"
            />
            <TextField
                value={passportSeries ?? ""}
                onChange={handlePassportSeriesChange}
                label="Серия паспорта"
                className="registration__input-element"
                error={!!errors.passportSeries && passportSeries !== null}
                helperText={passportSeries !== null && errors.passportSeries}
                required
                fullWidth
                margin="normal"
                slotProps={{
                    htmlInput: {
                        maxLength: 4,
                        inputMode: 'numeric',
                    }
                }}
            />
            <TextField
                value={passportNumber ?? ""}
                onChange={handlepassportNumberChange}
                label="Номер паспорта"
                className="registration__input-element"
                error={!!errors.passportNumber && passportNumber !== null}
                helperText={passportNumber !== null && errors.passportNumber}
                required
                fullWidth
                margin="normal"
                slotProps={{
                    htmlInput: {
                        maxLength: 6,
                        inputMode: 'numeric',
                    }
                }}
            />
            <PasswordInput
                label="Пароль"
                className="registration__input-element"
                value={password ?? ""}
                onChange={handlePasswordChange}
                error={!!errors.password && password !== null}
                helperText={password !== null && errors.password}
                required
                fullWidth
                margin="normal"
            />
            <PasswordInput
                label="Подтверждение пароля"
                className="registration__input-element"
                value={confirmPassword ?? ""}
                onChange={handleConfirmPasswordChange}
                error={!!errors.confirmPassword && confirmPassword !== null}
                helperText={confirmPassword !== null && errors.confirmPassword}
                required
                fullWidth
                margin="normal"
            />
            <FormControl>
                <FormControlLabel
                    style={{ textAlign: 'left' }}
                    control={
                        <Checkbox
                            checked={userAgreement}
                            onChange={handleUserAgreementChange}
                            required
                        />
                    }
                    label={
                        <span>
                            Я принимаю <a href="https://google.com">пользовательское соглашение</a>
                        </span>
                    }
                />
            </FormControl>
            <FormControl>
                <FormControlLabel
                    style={{ textAlign: 'left' }}
                    control={
                        <Checkbox
                            checked={personalData}
                            onChange={handlePersonalDataChange}
                            required
                        />
                    }
                    label={
                        <span>
                            Я соглашаюсь на <a href="https://google.com">обработку персональных данных</a>
                        </span>
                    }
                >
                </FormControlLabel>
            </FormControl>
            <FormControl>
                <FormControlLabel
                    style={{ textAlign: 'left' }}
                    control={
                        <Checkbox
                            checked={auctionRules}
                            onChange={handleAuctionRulesChange}
                            required
                        />
                    }
                    label={
                        <span>
                            Я принимаю <a href="https://google.com">правила аукциона</a>
                        </span>
                    }
                />
            </FormControl>
        </React.Fragment>
    )
})